import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaUserPlus, FaTrash, FaUsers, FaCopy } from 'react-icons/fa';
import { API_URL } from '../config';
import ConfirmDialog from './ConfirmDialog';

// ============================================
// PORTAL — PEOPLE & ACCESS
// The client who OWNS a project (and anyone with edit rights on it) manages who can
// see that project. Mirrors the admin "People & Access" tab, never shows internal
// notes. Backend rules (routes/projects.js):
//   GET    /projects/:id/member-options — the "who can I add" picker
//   POST   /projects/:id/members        — staff, the owning client, or an editor
//   DELETE /projects/members/:id        — staff, a manager of that company, the owner, or self
// Props: projectId, members, canManage, onChanged
// ============================================

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const ROLE_LABEL = { viewer: 'Viewer', editor: 'Editor', manager: 'Manager' };
const ROLE_COLOUR = { viewer: '#64748B', editor: '#2563EB', manager: '#00A86B' };

// invited = the row exists but no login is linked yet · expired = the access window passed
const memberStatus = (m) => {
  if (m.expires_at && new Date(m.expires_at) < new Date()) return 'expired';
  if (!m.user_id) return 'invited';
  return 'active';
};

const EMPTY_FORM = {
  mode: 'person', user_id: '', name: '', email: '', company_type: '', company_id: '',
  member_role: 'editor', access_type: 'permanent', expires_at: '', projects_only: false
};

export default function PortalProjectMembers({ projectId, members = [], canManage = false, onChanged }) {
  const [companies, setCompanies] = useState([]);
  const [people, setPeople] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmMember, setConfirmMember] = useState(null);
  const [inviteLink, setInviteLink] = useState('');

  const auth = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`
  });

  // Best-effort: labels company rows and fills the picker. A non-owner editor gets a
  // 403 here, which is fine — rows just fall back to a generic label.
  const loadOptions = async () => {
    try {
      const r = await fetch(`${API_URL}/projects/${projectId}/member-options`, { headers: auth() });
      const d = await r.json();
      if (d.success) { setCompanies(d.companies || []); setPeople(d.people || []); }
    } catch { /* stay generic */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectId) loadOptions(); }, [projectId]);

  const companyName = (m) => (companies.find((c) => c.id === m.company_id) || {}).name || '';

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setInviteLink('');
    setShowAdd(true);
    if (companies.length === 0 && people.length === 0) loadOptions();
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        member_type: 'person',
        member_role: form.member_role,
        access_type: form.access_type,
        expires_at: form.access_type === 'temporary' ? (form.expires_at || null) : null,
        // External / temporary people only ever get the projects area.
        portal_scope: form.projects_only ? 'projects_only' : null
      };
      if (form.mode === 'invite') {
        if (!form.email.trim()) throw new Error('Enter their email address');
        payload.name = form.name.trim() || null;
        payload.email = form.email.trim();
        payload.company_type = form.company_type || null;
        payload.company_id = form.company_id || null;
      } else {
        const [uid, ctype, cid] = String(form.user_id).split('|');
        if (!uid) throw new Error('Pick a person to add');
        payload.user_id = uid;
        payload.company_type = ctype || null;
        payload.company_id = cid || null;
      }

      const r = await fetch(`${API_URL}/projects/${projectId}/members`, {
        method: 'POST', headers: auth(), body: JSON.stringify(payload)
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Could not add access');

      setShowAdd(false);
      // Email is not configured locally — hand the sign-in link over so it can be shared.
      if (d.invited && d.invited.invite_link && !d.invited.email_sent) setInviteLink(d.invited.invite_link);
      toast.success(d.invited && d.invited.email_sent ? 'Access added - invitation emailed' : 'Access added');
      if (onChanged) onChanged();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const doRemove = async () => {
    const m = confirmMember;
    if (!m) return;
    try {
      const r = await fetch(`${API_URL}/projects/members/${m.id}`, { method: 'DELETE', headers: auth() });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Could not remove access');
      toast.success('Access removed');
      if (onChanged) onChanged();
    } catch (err) { toast.error(err.message); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success('Invite link copied'); }
    catch { toast.info(inviteLink); }
  };

  return (
    <div className="bg-white rounded-2xl border p-4 mt-3 shrink-0" style={{ borderColor: '#E8ECEF' }}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-navy flex items-center gap-2">
          <FaUsers className="text-green" /> People &amp; access ({members.length})
        </h2>
        {canManage && (
          <button onClick={openAdd} className="px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy flex items-center gap-1">
            <FaUserPlus size={10} /> Add person
          </button>
        )}
      </div>

      {inviteLink && (
        <div className="mb-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
          <p className="text-xs text-amber-800 mb-1">Email is not configured - share this sign-in link with them directly:</p>
          <div className="flex items-center gap-2">
            <code className="text-[11px] text-navy bg-white rounded px-2 py-1 flex-1 truncate border border-amber-200">{inviteLink}</code>
            <button onClick={copyLink} className="text-xs px-2 py-1 rounded-lg bg-navy text-white flex items-center gap-1"><FaCopy size={10} /> Copy</button>
          </div>
        </div>
      )}

      {members.length === 0 ? (
        <p className="text-sm text-gray-400">No one added yet - add the people who should see this project.</p>
      ) : (
        <div className="space-y-1">
          {members.map((m) => {
            const role = m.member_role || 'editor';
            const isCompanyRow = m.member_type === 'company';
            const status = memberStatus(m);
            const cn = companyName(m);
            const colour = ROLE_COLOUR[role] || ROLE_COLOUR.editor;
            return (
              <div key={m.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-navy truncate">
                    {isCompanyRow ? `${cn || 'Company'} (everyone)` : (m.name || m.email || 'Person')}
                    <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${colour}1A`, color: colour }}>
                      {ROLE_LABEL[role] || 'Editor'}
                    </span>
                    {status === 'invited' && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">Invited</span>}
                    {status === 'expired' && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">Expired</span>}
                    {status === 'active' && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green/10 text-green">Active</span>}
                    {isCompanyRow && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Company</span>}
                    {m.portal_scope === 'projects_only' && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Projects only</span>}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {!isCompanyRow && cn ? `${cn} - ` : ''}
                    {m.company_role ? `${m.company_role} - ` : ''}
                    {m.access_type === 'temporary' ? `Temporary${m.expires_at ? ` until ${fmtDate(m.expires_at)}` : ''}` : 'Permanent'}
                  </p>
                </div>
                {canManage && (
                  <button onClick={() => setConfirmMember(m)} className="text-red-500 hover:text-red-700 shrink-0 ml-2" title="Remove access">
                    <FaTrash size={11} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)}></div>
          <form onSubmit={submit} className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-1">Add person</h2>
            <p className="text-xs text-gray-500 mb-4">
              <strong>Viewer</strong> = read-only · <strong>Editor</strong> = can add, and edit what they added.
            </p>

            <div className="flex gap-2 mb-4">
              {[{ k: 'person', label: 'Existing person' }, { k: 'invite', label: 'Invite by email' }].map((mo) => (
                <button
                  key={mo.k}
                  type="button"
                  onClick={() => setForm({ ...form, mode: mo.k })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${form.mode === mo.k ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >{mo.label}</button>
              ))}
            </div>

            <div className="space-y-3">
              {form.mode === 'person' ? (
                <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">Pick a person…</option>
                  {people.length > 0 && (
                    <optgroup label="One Purpose Logistics">
                      {people.map((p) => (<option key={p.user_id} value={`${p.user_id}|op|`}>{p.label}</option>))}
                    </optgroup>
                  )}
                  {companies.filter((c) => (c.people || []).length).map((c) => (
                    <optgroup key={c.id} label={c.name}>
                      {c.people.map((p) => (
                        <option key={p.user_id} value={`${p.user_id}|${c.type}|${c.id}`}>{p.label}{p.role_label ? ` - ${p.role_label}` : ''}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              ) : (
                <>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (they get an invitation)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Access</label>
                  <select value={form.member_role} onChange={(e) => setForm({ ...form, member_role: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                  <select value={form.access_type} onChange={(e) => setForm({ ...form, access_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="permanent">Permanent</option>
                    <option value="temporary">Temporary</option>
                  </select>
                </div>
              </div>

              {form.access_type === 'temporary' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Access ends</label>
                  <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              )}

              <label className="flex items-start gap-2 text-xs text-gray-600">
                <input type="checkbox" className="mt-0.5" checked={form.projects_only} onChange={(e) => setForm({ ...form, projects_only: e.target.checked })} />
                <span><strong>Projects area only</strong> - they only see projects, not quotes, invoices or reports. Best for external or temporary people.</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-green hover:bg-green-600 disabled:opacity-50">{saving ? 'Adding…' : 'Add person'}</button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmMember}
        onClose={() => setConfirmMember(null)}
        title="Remove access?"
        message={`Remove ${(confirmMember && (confirmMember.name || confirmMember.email)) || 'this person'} from this project? They will no longer see it.`}
        confirmLabel="Remove"
        danger
        onConfirm={doRemove}
      />
    </div>
  );
}



